// The native picker, wearing `Field`'s skin. Children are `<option>`s.
//
// **Native `<select>`, not MUI's.** MUI's is a portalled listbox with its own
// focus management; the native one already opens dark (`color-scheme: dark` in
// `index.html`) and is the only control on the page a phone renders as a
// system wheel, which is what a picker should do there.
//
// **The chevron is drawn, not a data URI.** A `background-image: url("data:…")`
// cannot read a token — the arrow's colour would be a hex frozen into the
// primitive, which is the drift `kit.test.ts` exists to stop. So the wrapper
// carries a border-triangle in `inkDim` and the select carries the padding
// that leaves room for it.

import { styled } from "@mui/material/styles";
import { forwardRef, type ComponentProps } from "react";

import { motion } from "./motion";

const Wrap = styled("span")({
  position: "relative",
  display: "block",
  width: "100%"
});

const Native = styled("select")(({ theme }) => {
  const c = theme.kit.color;
  return {
    appearance: "none",
    width: "100%",
    boxSizing: "border-box",
    background: `color-mix(in srgb, ${c.panelRaised} 85%, transparent)`,
    border: `2px solid ${c.line}`,
    borderRadius: theme.kit.radius.md,
    color: c.ink,
    fontFamily: theme.kit.font.body,
    fontSize: 14,
    lineHeight: 1.4,
    // Right padding clears the chevron.
    padding: "8px 30px 8px 12px",
    cursor: "pointer",
    ...motion([], ["border-color"]),
    "&:focus": { outline: "none", borderColor: c.brand },
    "&:disabled": { opacity: 0.38, pointerEvents: "none" },
    "@media (pointer: coarse)": { minHeight: theme.kit.touchTarget }
  };
});

/** The arrow. No pointer, or it eats the click that opens the list. */
const Chevron = styled("span")(({ theme }) => ({
  position: "absolute",
  right: 12,
  top: "50%",
  marginTop: -2,
  width: 0,
  height: 0,
  pointerEvents: "none",
  borderLeft: "4px solid transparent",
  borderRight: "4px solid transparent",
  borderTop: `5px solid ${theme.kit.color.inkDim}`
}));

export type SelectProps = ComponentProps<"select">;

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { children, ...rest },
  ref
) {
  return (
    <Wrap>
      <Native ref={ref} {...rest}>
        {children}
      </Native>
      <Chevron />
    </Wrap>
  );
});
